import React, { useState, useEffect, useCallback } from "react";
import { api } from "@/lib/api";
import { useCurrency } from "@/context/CurrencyContext";
import {
  PageHeader, SectionCard, Money, Loading, EmptyState, KpiCard, StatusBadge,
} from "@/components/common/ui";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import {
  Table, TableHeader, TableBody, TableHead, TableRow, TableCell,
} from "@/components/ui/table";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import {
  ShieldCheck, RefreshCw, CheckCircle2, AlertTriangle, Building2, Layers3,
  CircleDollarSign,
} from "lucide-react";

const DASH = "\u2014";

const PERIODS = [
  { key: "FY", label: "Year" },
  { key: "H1", label: "H1" },
  { key: "H2", label: "H2" },
  { key: "Q1", label: "Q1" },
  { key: "Q2", label: "Q2" },
  { key: "Q3", label: "Q3" },
  { key: "Q4", label: "Q4" },
];

const SOURCE_LABEL = { dashboard: "Dashboard", project: "Project", analytics: "Analytics" };

// One metric cell: shows the reconciled value + tick when aligned, or the three
// source values + the drift delta when they diverge.
function MetricCell({ cmp, testId }) {
  const { money } = useCurrency();
  if (!cmp) return <span className="text-muted-foreground">{DASH}</span>;
  if (cmp.match) {
    return (
      <div className="flex items-center justify-end gap-1.5" data-testid={testId} data-state="match">
        <Money value={cmp.analytics} className="text-sm" />
        <CheckCircle2 className="h-3.5 w-3.5 shrink-0 text-[hsl(var(--success))]" aria-label="aligned" />
      </div>
    );
  }
  return (
    <div className="text-right" data-testid={testId} data-state="drift">
      <div className="flex items-center justify-end gap-1 font-medium text-[hsl(var(--danger))]">
        <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
        <span className="tabular-nums">Δ {money(cmp.max_delta)}</span>
      </div>
      <div className="mt-0.5 text-[11px] leading-tight text-muted-foreground">
        {["dashboard", "project", "analytics"].map((s) => (
          <span key={s} className="mr-2 inline-block">
            <span className="opacity-70">{SOURCE_LABEL[s][0]}</span>{" "}
            <span className="tabular-nums text-foreground">{money(cmp[s])}</span>
          </span>
        ))}
      </div>
    </div>
  );
}

// Planned Budget cell: validates the 80%-of-Allocated rule + cross-surface parity.
// Shows the reconciled value + tick when Dashboard == Project == 80%×Allocated;
// otherwise surfaces the three figures (Dashboard / Project / 80% expected).
function PlannedCell({ cmp, testId }) {
  const { money } = useCurrency();
  if (!cmp) return <span className="text-muted-foreground">{DASH}</span>;
  if (cmp.match) {
    return (
      <div className="flex items-center justify-end gap-1.5" data-testid={testId} data-state="match">
        <Money value={cmp.dashboard} className="text-sm" />
        <CheckCircle2 className="h-3.5 w-3.5 shrink-0 text-[hsl(var(--success))]" aria-label="80% rule ok" />
      </div>
    );
  }
  return (
    <div className="text-right" data-testid={testId} data-state="drift">
      <div className="flex items-center justify-end gap-1 font-medium text-[hsl(var(--warning))]">
        <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
        <span className="tabular-nums">{cmp.rule_ok ? "\u0394" : "\u2260 80%"} {money(cmp.max_delta)}</span>
      </div>
      <div className="mt-0.5 text-[11px] leading-tight text-muted-foreground">
        <span className="mr-2 inline-block">D <span className="tabular-nums text-foreground">{money(cmp.dashboard)}</span></span>
        <span className="mr-2 inline-block">P <span className="tabular-nums text-foreground">{money(cmp.project)}</span></span>
        <span className="inline-block">80% <span className="tabular-nums text-foreground">{money(cmp.expected)}</span></span>
      </div>
    </div>
  );
}

export default function DataIntegrity() {
  const { money } = useCurrency();
  const [period, setPeriod] = useState("FY");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const run = useCallback(async (p) => {
    setLoading(true);
    setError(false);
    try {
      const d = await api.parityCheck(p);
      setData(d);
      if (d?.summary?.all_ok) {
        toast.success("Parity sweep passed", { description: `All ${d.summary.projects_total} projects aligned across Dashboard, Project & Analytics.` });
      } else {
        const n = d?.summary?.projects_failed || 0;
        toast.warning("Parity drift detected", { description: `${n} project(s) or corporate totals need attention.` });
      }
    } catch (e) {
      setError(true);
      toast.error("Could not run parity sweep", { description: "Please retry in a moment." });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { run(period); }, [period, run]);

  const s = data?.summary;
  const corp = data?.corporate;
  const allOk = !!s?.all_ok;

  return (
    <div className="space-y-5" data-testid="data-integrity-page">
      <PageHeader
        title="Data Integrity"
        description="One-click parity sweep — confirms Spend, Budget Allocated & Revenue match across the Dashboard, Project Detail and Analytics for every project, and that corporate totals reconcile."
      >
        <ToggleGroup
          type="single" value={period}
          onValueChange={(v) => v && setPeriod(v)}
          className="rounded-lg border bg-card p-0.5"
          data-testid="parity-period-switcher"
        >
          {PERIODS.map((p) => (
            <ToggleGroupItem
              key={p.key} value={p.key}
              className="h-7 px-2.5 text-xs data-[state=on]:bg-primary data-[state=on]:text-primary-foreground"
              data-testid={`parity-period-${p.key}`}
            >
              {p.label}
            </ToggleGroupItem>
          ))}
        </ToggleGroup>
        <Button
          onClick={() => run(period)} disabled={loading}
          className="gap-1.5" data-testid="parity-run-button"
        >
          <RefreshCw className={cn("h-4 w-4", loading && "animate-spin")} />
          {loading ? "Running\u2026" : "Run sweep"}
        </Button>
      </PageHeader>

      {loading && !data && <Loading rows={6} />}

      {error && !loading && (
        <EmptyState
          title="Sweep unavailable"
          message="The parity check could not be completed. Please retry."
          action={<Button onClick={() => run(period)} data-testid="parity-retry-button"><RefreshCw className="mr-1.5 h-4 w-4" /> Retry</Button>}
        />
      )}

      {data && !error && (
        <>
          {/* Summary tiles */}
          <div className="grid grid-cols-2 gap-3 xl:grid-cols-4" data-testid="parity-summary-strip">
            <KpiCard
              testId="parity-overall-status"
              label="Overall status"
              accent={allOk ? "green" : "red"}
              icon={allOk ? CheckCircle2 : AlertTriangle}
              value={<span data-testid="parity-overall-value">{allOk ? "All aligned" : `${s.projects_failed} to review`}</span>}
              sub={allOk ? "Dashboard = Project = Analytics" : "Drift detected in one or more sources"}
            />
            <KpiCard
              testId="parity-projects-checked"
              label="Projects checked"
              accent="teal"
              icon={Layers3}
              value={<span data-testid="parity-projects-checked-value">{s.projects_total}</span>}
              sub={`${s.projects_ok} passed \u00b7 ${s.projects_failed} failed`}
            />
            <KpiCard
              testId="parity-metric-failures"
              label="Metric failures"
              accent={(s.metric_fail_counts.spend + s.metric_fail_counts.budget + s.metric_fail_counts.revenue + (s.metric_fail_counts.planned || 0)) ? "red" : "slate"}
              icon={CircleDollarSign}
              value={<span data-testid="parity-metric-failures-value">{s.metric_fail_counts.spend + s.metric_fail_counts.budget + s.metric_fail_counts.revenue + (s.metric_fail_counts.planned || 0)}</span>}
              sub={`Spend ${s.metric_fail_counts.spend} \u00b7 Budget ${s.metric_fail_counts.budget} \u00b7 Rev ${s.metric_fail_counts.revenue} \u00b7 Plan ${s.metric_fail_counts.planned || 0}`}
            />
            <KpiCard
              testId="parity-corporate-status"
              label="Corporate consistency"
              accent={corp?.match ? "green" : "red"}
              icon={Building2}
              value={<span data-testid="parity-corporate-value">{corp?.match ? "Reconciled" : "Mismatch"}</span>}
              sub={corp?.match ? "Overview = \u03a3 entity totals" : `Unattributed ${money(corp?.unattributed || 0)}`}
            />
          </div>

          {/* Per-project parity table */}
          <SectionCard
            title="Per-project parity"
            description={`Spend, Budget Allocated, Revenue & Planned (80% of Allocated) compared across all surfaces \u00b7 tolerance ${money(data.tolerance)}.`}
            testId="parity-projects-card"
            action={
              <div className="flex items-center gap-2">
                <Badge variant="outline" className={cn("text-[10px] font-medium",
                  s.planned_rule_ok
                    ? "border-[hsl(var(--success)/0.3)] bg-[hsl(var(--success)/0.1)] text-[hsl(var(--success))]"
                    : "border-[hsl(var(--warning)/0.3)] bg-[hsl(var(--warning)/0.12)] text-[hsl(var(--warning))]")}
                  data-testid="parity-planned-rule-badge">
                  Planned = 80% rule: {data.planned_rule.projects_ok}/{data.planned_rule.projects_total}
                </Badge>
                <StatusBadge status={allOk ? "ok" : "exceeded"} label={allOk ? "All aligned" : "Drift"} testId="parity-projects-badge" />
              </div>
            }
          >
            <div className="table-scroll overflow-x-auto">
              <Table data-testid="parity-projects-table">
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-xs font-semibold uppercase tracking-wide">Project</TableHead>
                    <TableHead className="text-right text-xs font-semibold uppercase tracking-wide">Spend</TableHead>
                    <TableHead className="text-right text-xs font-semibold uppercase tracking-wide">Budget Allocated</TableHead>
                    <TableHead className="text-right text-xs font-semibold uppercase tracking-wide">Revenue</TableHead>
                    <TableHead className="text-right text-xs font-semibold uppercase tracking-wide">Planned (80%)</TableHead>
                    <TableHead className="text-center text-xs font-semibold uppercase tracking-wide">Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.projects.map((row) => (
                    <TableRow
                      key={row.project}
                      data-testid={`parity-row-${row.project}`}
                      data-ok={row.ok ? "true" : "false"}
                      className={cn(!row.ok && "bg-[hsl(var(--danger)/0.05)]")}
                    >
                      <TableCell className="font-medium">
                        <div className="flex items-center gap-2">
                          <span>{row.project}</span>
                          {row.is_corporate && (
                            <Badge variant="outline" className="border-[hsl(var(--info)/0.3)] bg-[hsl(var(--info)/0.1)] text-[10px] font-medium text-[hsl(var(--info))]" data-testid={`parity-corp-tag-${row.project}`}>
                              Corporate
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-right"><MetricCell cmp={row.spend} testId={`parity-spend-${row.project}`} /></TableCell>
                      <TableCell className="text-right"><MetricCell cmp={row.budget} testId={`parity-budget-${row.project}`} /></TableCell>
                      <TableCell className="text-right"><MetricCell cmp={row.revenue} testId={`parity-revenue-${row.project}`} /></TableCell>
                      <TableCell className="text-right"><PlannedCell cmp={row.planned} testId={`parity-planned-${row.project}`} /></TableCell>
                      <TableCell className="text-center">
                        <StatusBadge status={row.ok ? "ok" : "exceeded"} label={row.ok ? "Aligned" : "Drift"} testId={`parity-status-${row.project}`} />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </SectionCard>

          {/* Corporate consistency */}
          {corp && (
            <SectionCard
              title="Corporate KPI consistency"
              description="The Corporate Spends total must equal the sum of the entity totals rendered on the dashboard (Sumadhura / Signet / Tech)."
              testId="parity-corporate-card"
              action={<StatusBadge status={corp.match ? "ok" : "exceeded"} label={corp.match ? "Reconciled" : "Mismatch"} testId="parity-corporate-badge" />}
            >
              <div className="grid gap-4 p-4 md:grid-cols-2">
                <div className="space-y-2">
                  {corp.entities.map((e) => (
                    <div key={e.entity} className="flex items-center justify-between rounded-lg border bg-[hsl(var(--surface-2))] px-3 py-2" data-testid={`parity-corp-entity-${e.entity}`}>
                      <span className="text-sm text-muted-foreground">{e.entity}</span>
                      <Money value={e.total} className="text-sm" />
                    </div>
                  ))}
                  <div className="flex items-center justify-between rounded-lg border border-[hsl(var(--primary)/0.25)] bg-[hsl(var(--primary)/0.06)] px-3 py-2">
                    <span className="text-sm font-medium">Sum of entities</span>
                    <Money value={corp.entities_sum} className="text-sm font-semibold" testId="parity-corp-entities-sum" />
                  </div>
                </div>
                <div className="space-y-2">
                  <div className="flex items-center justify-between rounded-lg border bg-[hsl(var(--surface-2))] px-3 py-2">
                    <span className="flex items-center gap-1.5 text-sm text-muted-foreground"><Building2 className="h-4 w-4" /> Corporate overview total</span>
                    <Money value={corp.overview_total} className="text-sm font-semibold" testId="parity-corp-overview-total" />
                  </div>
                  <div className="flex items-center justify-between rounded-lg border bg-[hsl(var(--surface-2))] px-3 py-2">
                    <span className="text-sm text-muted-foreground">Unattributed spend</span>
                    <Money value={corp.unattributed} className={cn("text-sm font-semibold", corp.unattributed > 1 && "text-[hsl(var(--danger))]")} testId="parity-corp-unattributed" />
                  </div>
                  <div className={cn(
                    "flex items-center gap-2 rounded-lg border px-3 py-2.5 text-sm font-medium",
                    corp.match
                      ? "border-[hsl(var(--success)/0.25)] bg-[hsl(var(--success)/0.08)] text-[hsl(var(--success))]"
                      : "border-[hsl(var(--danger)/0.25)] bg-[hsl(var(--danger)/0.08)] text-[hsl(var(--danger))]"
                  )} data-testid="parity-corp-verdict">
                    {corp.match ? <CheckCircle2 className="h-4 w-4" /> : <AlertTriangle className="h-4 w-4" />}
                    {corp.match
                      ? `Reconciled \u2014 difference ${money(corp.delta)}`
                      : `Off by ${money(corp.delta)} \u2014 investigate corporate attribution`}
                  </div>
                </div>
              </div>
            </SectionCard>
          )}

          <p className="flex items-center gap-1.5 px-1 text-[11px] text-muted-foreground" data-testid="parity-footnote">
            <ShieldCheck className="h-3.5 w-3.5" />
            Values are pulled live from the same endpoints powering each page. Last run {new Date(data.generated_at).toLocaleString()}.
          </p>
        </>
      )}
    </div>
  );
}
